import { compile } from 'path-to-regexp';

import type { ExternalChainExtended } from 'types/externalChains';

import config from 'configs/app';

import getResourceParams from './getResourceParams';
import isNeedProxy from './isNeedProxy';
import type { ResourceName, ResourcePathParams } from './resources';

export default function buildUrl<R extends ResourceName>(
  resourceFullName: R,
  pathParams?: ResourcePathParams<R>,
  queryParams?: Record<string, string | Array<string> | number | boolean | null | undefined>,
  noProxy?: boolean,
  chain?: ExternalChainExtended,
): string {
  const { api, resource } = getResourceParams(resourceFullName, chain);
  const useProxy = !noProxy && isNeedProxy();
  // routed through pages/api/proxy.ts (see nextjs/rewrites.js) so the request
  // stays same-origin and any cookies/credentials survive the request
  const baseUrl = useProxy ? config.app.baseUrl + '/node-api/proxy' : api.endpoint;
  const basePath = api.basePath ?? '';
  const path = basePath + resource.path;
  const compiledPath = compile(path)(pathParams);
  // new URL(path, base) treats a leading "/" in `path` as absolute and discards base's
  // own path (e.g. "/node-api/proxy"), so it must be resolved against the origin only,
  // then joined onto baseUrl's full path manually instead of relying on base resolution
  const url = new URL(baseUrl + compiledPath);
  queryParams && Object.entries(queryParams).forEach(([ key, value ]) => {
    // there are some pagination params that can be null or false for the next page
    value !== undefined && value !== '' && url.searchParams.append(key, String(value));
  });

  return url.toString();
}
